class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    this.thumbsRow = this.querySelector('.product-gallery__thumbs');
    if (!this.slides.length) return;

    this.current = Number(this.dataset.activeIndex) || 0;

    const canHover = window.matchMedia('(hover: hover)').matches;
    this.thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => this.show(i));
      if (canHover) thumb.addEventListener('mouseenter', () => this.show(i));
    });

    // Load every slide once the shopper starts browsing thumbnails, so hover swaps don't flash
    this.thumbsRow?.addEventListener('pointerenter', () => this.preload(), { once: true });
    this.thumbsRow?.addEventListener('focusin', () => this.preload(), { once: true });

    this.initSwipe();
    this.initZoom();

    this.onVariantChange = this.onVariantChange.bind(this);
    document.addEventListener('variant:change', this.onVariantChange);
  }

  disconnectedCallback() {
    document.removeEventListener('variant:change', this.onVariantChange);
  }

  show(index) {
    const target = (index + this.slides.length) % this.slides.length;
    if (target === this.current) return;
    this.current = target;

    this.slides.forEach((slide, i) => {
      const active = i === target;
      slide.classList.toggle('is-active', active);
      slide.hidden = !active;
      if (active) slide.querySelector('img')?.setAttribute('loading', 'eager');
    });

    this.thumbs.forEach((thumb, i) => {
      const active = i === target;
      thumb.classList.toggle('is-active', active);
      if (active) {
        thumb.setAttribute('aria-current', 'true');
        this.scrollThumbIntoView(thumb);
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
  }

  scrollThumbIntoView(thumb) {
    const row = this.thumbsRow;
    if (!row || row.scrollWidth <= row.clientWidth) return;
    const left = thumb.offsetLeft - (row.clientWidth - thumb.clientWidth) / 2;
    row.scrollTo({ left, behavior: 'smooth' });
  }

  preload() {
    this.slides.forEach((slide) => slide.querySelector('img')?.setAttribute('loading', 'eager'));
  }

  // Pick the variant's assigned image, else an image whose alt text matches the variant title or one of its options
  onVariantChange(event) {
    const { variant, options = [] } = event.detail || {};
    const mediaId = variant?.featured_media?.id;

    let index = mediaId ? this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId)) : -1;
    if (index === -1 && variant) index = this.slides.findIndex((slide) => slide.dataset.alt === variant.title);
    if (index === -1) index = this.slides.findIndex((slide) => slide.dataset.alt && options.includes(slide.dataset.alt));

    if (index > -1) this.show(index);
  }

  initSwipe() {
    const main = this.querySelector('.product-gallery__main');
    if (!main || this.slides.length < 2) return;

    let startX = 0;
    let startY = 0;
    main.addEventListener('touchstart', (event) => {
      startX = event.touches[0].clientX;
      startY = event.touches[0].clientY;
    }, { passive: true });

    main.addEventListener('touchend', (event) => {
      const dx = event.changedTouches[0].clientX - startX;
      const dy = event.changedTouches[0].clientY - startY;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
      this.show(this.current + (dx < 0 ? 1 : -1));
    }, { passive: true });
  }

  initZoom() {
    this.dialog = this.querySelector('[data-zoom-dialog]');
    const trigger = this.querySelector('[data-gallery-zoom]');
    if (!this.dialog || !trigger || typeof this.dialog.showModal !== 'function') {
      trigger?.remove();
      return;
    }

    this.stage = this.dialog.querySelector('[data-zoom-stage]');
    this.zoomImg = this.dialog.querySelector('[data-zoom-img]');
    this.zoomIndex = this.current;

    const open = () => {
      this.showZoom(this.current);
      this.dialog.showModal();
      document.documentElement.classList.add('product-zoom-open');
    };

    trigger.addEventListener('click', open);
    this.slides.forEach((slide) => slide.addEventListener('click', open));

    this.dialog.querySelector('[data-zoom-close]')?.addEventListener('click', () => this.dialog.close());
    this.dialog.querySelector('[data-zoom-prev]')?.addEventListener('click', () => this.showZoom(this.zoomIndex - 1));
    this.dialog.querySelector('[data-zoom-next]')?.addEventListener('click', () => this.showZoom(this.zoomIndex + 1));

    // Clicking the dark backdrop closes; clicking the image toggles magnification
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog || event.target === this.stage) this.dialog.close();
    });

    this.zoomImg.addEventListener('click', (event) => {
      const zoomed = this.dialog.classList.toggle('is-zoomed');
      if (zoomed) this.panTo(event);
    });
    this.stage.addEventListener('pointermove', (event) => {
      if (this.dialog.classList.contains('is-zoomed')) this.panTo(event);
    });

    this.dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.showZoom(this.zoomIndex - 1);
      if (event.key === 'ArrowRight') this.showZoom(this.zoomIndex + 1);
    });

    // Leave the page gallery on whichever image was last viewed in the modal
    this.dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('product-zoom-open');
      this.dialog.classList.remove('is-zoomed');
      this.show(this.zoomIndex);
    });
  }

  showZoom(index) {
    this.zoomIndex = (index + this.slides.length) % this.slides.length;
    const slide = this.slides[this.zoomIndex];
    this.dialog.classList.remove('is-zoomed');
    this.zoomImg.style.transformOrigin = '';
    this.zoomImg.src = slide.dataset.zoomSrc;
    this.zoomImg.alt = slide.querySelector('img')?.alt || '';
  }

  // Measured against the untransformed stage so the origin doesn't feed back into the scaled image's box
  panTo(event) {
    const rect = this.stage.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    this.zoomImg.style.transformOrigin = `${Math.min(100, Math.max(0, x))}% ${Math.min(100, Math.max(0, y))}%`;
  }
}

customElements.define('product-gallery', ProductGallery);
